/* eslint-disable @typescript-eslint/no-explicit-any */
import { create } from "zustand";
import {
  RealtimeData,
  RealTimeStoreType,
  Subscription,
} from "../../types/stores/RealtimeStore";

export const useRealTimeStore = create<RealTimeStoreType>((set) => {
  return {
    activeSubscriptions: [],
    addSubscription: (newSubscription) =>
      set(
        (state) => ({
          ...state,
          activeSubscriptions: updateSubscriptions(
            state.activeSubscriptions,
            newSubscription,
          ),
        }),
        true as any,
      ),
    removeSubscription: (removedSubscription) =>
      set(
        (state) => ({
          ...state,
          activeSubscriptions: removeSubscription(
            state.activeSubscriptions,
            removedSubscription,
          ),
        }),
        true as any,
      ),
    isSubscribed: (newSubscription) => {
      const activeSubscriptions: Subscription[] =
        useRealTimeStore.getState().activeSubscriptions;
      return isSubscribed(activeSubscriptions, newSubscription);
    },
    lastData: {},
    getSensorData: (sensorId: string): RealtimeData | undefined => {
      const _lastData = useRealTimeStore.getState().lastData;
      return _lastData[sensorId];
    },
    mergedLastData: [],
    updateSensorLastData: (sensorId: string, newContent: RealtimeData) => {
      set((state) => {
        const lastData = updateSensorLastData(
          sensorId,
          newContent,
          state.lastData,
        );
        return {
          ...state,
          lastData,
          mergedLastData: getMergedData(lastData),
          lastTimeUpdated: new Date(Date.now()),
        };
      }, true as any);
    },
    removeLastDataEntry: (id: string) =>
      set((state) => {
        const lastData = removeLastDataEntry(id, state.lastData);
        return {
          ...state,
          lastData,
          mergedLastData: getMergedData(lastData),
        };
      }, true as any),
    removeLastDataEntries: (ids: string[]) =>
      set((state) => {
        const lastData = removeLastDataEntries(ids, state.lastData);
        return {
          ...state,
          lastData,
          mergedLastData: getMergedData(lastData),
        };
      }, true as any),
    lastTimeUpdated: new Date(),
  };

  function isSubscribed(
    activeSubscriptions: Subscription[],
    newSubscription: Subscription,
  ): boolean {
    return (
      activeSubscriptions.findIndex((current) =>
        isSameSubscription(current, newSubscription),
      ) !== -1
    );
  }

  function updateSubscriptions(
    activeSubscriptions: Subscription[],
    newSubscription: Subscription,
  ) {
    if (!activeSubscriptions || !newSubscription) {
      return activeSubscriptions;
    }
    const copyOfActiveSubscription = [...activeSubscriptions];
    const index = activeSubscriptions.findIndex((current) =>
      isSameSubscription(current, newSubscription),
    );

    if (index === -1) {
      copyOfActiveSubscription.push(newSubscription);
    } else {
      copyOfActiveSubscription[index] = newSubscription;
    }

    return copyOfActiveSubscription;
  }

  function removeSubscription(
    activeSubscriptions: Subscription[],
    removedSubscription: Subscription,
  ) {
    if (!activeSubscriptions || !removedSubscription) {
      return activeSubscriptions;
    }
    const copyOfActiveSubscription = [...activeSubscriptions];
    const index = activeSubscriptions.findIndex((current) =>
      isSameSubscription(current, removedSubscription),
    );

    if (index !== -1) {
      copyOfActiveSubscription.splice(index, 1);
    }

    return copyOfActiveSubscription;
  }

  function removeLastDataEntry(
    id: string,
    lastData: Record<string, RealtimeData>,
  ): Record<string, RealtimeData> {
    const _lastData = { ...lastData };
    delete _lastData[id];
    return _lastData;
  }

  function removeLastDataEntries(
    ids: string[],
    lastData: Record<string, RealtimeData>,
  ): Record<string, RealtimeData> {
    const _lastData = { ...lastData };
    ids.forEach((id) => {
      delete _lastData[id];
    });
    return _lastData;
  }

  function updateSensorLastData(
    id: string,
    newContent: RealtimeData,
    lastData: Record<string, RealtimeData>,
  ): Record<string, RealtimeData> {
    return {
      ...lastData,
      [id]: { ...newContent, sensorId: id },
    };
  }

  function getMergedData(
    lastData: Record<string, RealtimeData>,
  ): RealtimeData[] {
    return Object.values(lastData);
  }

  function isSameSubscription(
    left: Subscription,
    right: Subscription,
  ): boolean {
    return (
      left.entity.modelName === right.entity.modelName &&
      JSON.stringify(normalizeIds(left.options.id)) ===
        JSON.stringify(normalizeIds(right.options.id))
    );
  }

  function normalizeIds(ids: string | string[]) {
    return Array.isArray(ids) ? [...ids].sort() : [ids];
  }
});
